import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from "../assets/GenZ_Logo.svg";
import { useParams, useNavigate } from 'react-router-dom';
import SocialMediaContainer from '../components/SocialMediaContainer';

const BloomForm = () => {
  const { referralCode: referralCodeFromUrl } = useParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    referralCode: referralCodeFromUrl || '',
    affiliation: '',
    role: 'Student',
    location: '',
  });
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [isFormLocked, setIsFormLocked] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => { document.title = 'Bloom360 Sign Up'; }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAffiliation = (value) => {
    const affiliationRegex = /^[a-zA-Z0-9\s&.\-]+$/;
    return (
      affiliationRegex.test(value) &&
      value.trim().length > 0 &&
      value.length <= 50
    );
  };

  const handleChange = (e) => {
    if (isFormLocked) return;
    const { name, value } = e.target;
    if (name === 'email') {
      if (!validateEmail(value)) {
        setEmailError('Invalid email address');
      } else {
        setEmailError('');
      }
    }
    if (name === "affiliation") {
      if (validateAffiliation(value) || value === "") {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (emailError) {
      setMessage('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }
    try {
      const response = await axios.post('https://360betasignup.feeltiptop.com/api/bloom360-subscribe', formData);
      if (response.data.success) {
        setMessage('Please check your inbox for the confirmation link.');
        setIsSignedUp(true);
        setConfirmationEmail(formData.email);
        startResendTimer();
        setIsFormLocked(true);
      } else {
        setMessage(response.data.message);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('https://360betasignup.feeltiptop.com/api/bloom360-resend', formData);
      if (response.data.success) {
        setMessage(`Confirmation email resent to ${formData.email}.`);
        startResendTimer();
      } else {
        setMessage('Error resending confirmation email, ' + response.data.message + '. Please try again.');
      }
    } catch (error) {
      console.error('Error resending email:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startResendTimer = () => setResendTimer(30);

  const removeReferralCode = () => {
    setFormData((prevData) => ({ ...prevData, referralCode: '' }));
    navigate('/bloom360signup');
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
      return () => clearInterval(timerId);
    }
  }, [resendTimer]);

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.email.trim() !== '' &&
    !emailError;

  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-4 sm:p-6 md:p-8 bg-white overflow-auto">
      <div className="logo-container mb-6">
        <img src={logo} alt="GenZ Logo" className="w-16 sm:w-20 md:w-24" loading="lazy" />
      </div>
      <h2 className="text-4xl sm:text-4xl font-extrabold text-black mb-4 tracking-tight">Register for Early Access to Bloom360</h2>
      <p className="text-lg sm:text-2xl text-gray-700 mb-10 leading-relaxed">Bloom360 is built for students, recent graduates, and professors - empowering the next generation to grow and connect.</p>
      <a href="/Bloom360 Brochure.pdf" target="_blank" rel="noopener noreferrer" className="text-xl text-[#ff4f55] underline font-medium mb-10">Learn more about Bloom360 →</a>
      <div className="w-full max-w-6xl bg-[#F9FAFB] rounded-2xl p-8 sm:p-20">
        <form onSubmit={handleSubmit} className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div className="mb-4">
            <label htmlFor="firstName" className="block text-sm font-medium text-black mb-1 text-left">
              First Name <span className="text-[#ff4f55]">*</span>
            </label>
            <input type="text" name="firstName" value={formData.firstName} placeholder="Your first name" onChange={handleChange} maxLength={50} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} required />
          </div>

          <div className="mb-4">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Last Name <span className="text-[#ff4f55]">*</span>
            </label>
            <input type="text" name="lastName" placeholder="Your last name" value={formData.lastName} onChange={handleChange} maxLength={50} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} required />
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Email Address <span className="text-[#ff4f55]">*</span>
            </label>
            <input type="email" name="email" placeholder="Your email" value={formData.email} onChange={handleChange} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} maxLength={254} required />
            {emailError && <p className="text-[#ff4f55] text-sm mt-2">{emailError}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Primary Role</label>
            <div className="flex flex-col gap-2">
              {['Student', 'Recent Graduate', 'Professor'].map((option) => (
                <label key={option} className={`flex items-center gap-3 cursor-pointer text-sm text-gray-700 ${isFormLocked ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="radio" name="role" value={option} checked={formData.role === option} onChange={(e) => handleChange(e)} disabled={isFormLocked} className="w-4 h-4 accent-gray-800" />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="affiliation" className="block text-sm font-medium text-gray-700 mb-1 text-left">Affiliation</label>
            <input type="text" name="affiliation" placeholder="Your university or organization" value={formData.affiliation} onChange={handleChange} maxLength={50} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} />
            {!validateAffiliation(formData.affiliation) && formData.affiliation && (
              <p className="text-sm text-[#ff2a2f] mt-1 text-left">Please enter a valid affiliation (letters, numbers, &, ., -, and spaces only, max 50 characters).</p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1 text-left">Location</label>
            <input type="text" name="location" placeholder="City, State, Country" value={formData.location} onChange={handleChange} maxLength={100} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} />
          </div>

          <div className="mb-4">
            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1 text-left">Referral Code</label>
            <input type="text" name="referralCode" placeholder="Referral code (if any)" value={formData.referralCode} onChange={handleChange} maxLength={25} className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-black" disabled={isFormLocked} />
          </div>

          {formData.referralCode && (
            <div className="mb-4 text-center md:col-start-1">
              <p>Referral Code: {formData.referralCode}</p>
              <button type="button" onClick={removeReferralCode} hidden={isFormLocked} disabled={isFormLocked} className="text-[#ff2a2f] hover:text-[#ff4f55] font-bold">Remove Referral Code</button>
            </div>
          )}

          <div className="md:col-start-1">
            {isSignedUp ? (
              <div className="w-full">
                <p className="text-center mb-4 text-sm text-gray-600">Confirmation email sent to: {confirmationEmail}</p>
                <button onClick={handleResendEmail} className={`w-full p-3 bg-[#ff2a2f] text-white font-semibold rounded-lg hover:bg-[#ff4f55] ${isLoading || resendTimer > 0 ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={isLoading || resendTimer > 0}>
                  {resendTimer > 0 ? `Resend Email (${resendTimer}s)` : 'Resend Email'}
                </button>
              </div>
            ) : (
              <button type="submit" className={`w-60 px-10 py-3 rounded-full bg-black text-white font-semibold hover:bg-gray-800 transition ${!isFormValid || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!isFormValid || isLoading || isSignedUp}>
                {isLoading ? 'Signing up...' : 'Sign Up'}
              </button>
            )}
          </div>
        </form>
      </div>

      {message && <div className="mt-6 text-center text-md font-semibold text-gray-600">{message}</div>}
      <SocialMediaContainer />
      <p className="mt-6 text-center text-xs text-gray-600">We respect your privacy; this email will be used solely for launch updates and invitations to start building your profile with us.</p>
    </div>
  );
};

export default BloomForm;
